import React, { useState, useContext, useEffect } from "react";
import { sha256 } from "js-sha256";
import _ from "lodash";
import { toast } from "react-toastify";
import SpinnerComponent from "../@core/components/spinner/FallbackSpinner";
import {
  BusinessNotPermitted,
  isPathPubliclyAccessible,
  getAuthStateFromCache,
  getDataForAdmin,
  pathDoesNotRequireBusinessId,
  getBIDFromURL,
  getBIDFromCachedRedirectURL,
  removeFromCache,
  addUpdateCache,
} from "./authTools";
import { callApi } from "../api";

const authContext = React.createContext({
  selectedBusiness: {},
  user: {},
  business: [],
});

const { Provider } = authContext;
const initialAuthState = {
  user: null,
  forAdmin: null,
  newBusiness: [],
  selectedBusiness: {},
};

const getBusinessDetails = async (uid, bid) =>
  callApi(`/protected/user/${uid}/business/${bid}/detail?uid=${uid}`);

const useAuthProvider = () => {
  const [initializing, setInitializing] = useState(true);
  const [auth, setAuth] = useState(initialAuthState);
  const [isBusinessPermitted, setIsBusinessPermitted] = useState(true);
  const setAuthData = (key, value) => setAuth({ ...auth, [key]: value });

  const logOut = async (props) => {
    const redirectToProps = props?.redirectToProps;
    const logOutResponse = await callApi("/logout", "GET");
    removeFromCache(["redirectTo", "user", "business", "selectedBusiness"]);
    addUpdateCache({ loggingOut: "true" });
    if (redirectToProps) {
      addUpdateCache({
        redirectTo: JSON.stringify({
          bizID: redirectToProps.bid,
          path: redirectToProps.path,
        }),
      });
    }
    setAuth(initialAuthState);
    // eslint-disable-next-line consistent-return
    return logOutResponse;
  };

  useEffect(async () => {
    if (isPathPubliclyAccessible(window.location.pathname)) {
      setInitializing(false);
      return;
    }
    removeFromCache(["redirectTo", "loggingOut"]);
    const {
      user: cachedUser,
      business: cachedBusiness,
      selectedBusiness: cachedselectedBusiness,
    } = getAuthStateFromCache();

    let forAdmin;
    if (cachedUser?.role === "ADMIN") {
      const { users, businesses } = await getDataForAdmin();
      forAdmin = { users, businesses };
    }
    const newBusiness = cachedBusiness;
    const getSelectedBusiness = () => {
      if (!cachedUser) return null;
      if (cachedselectedBusiness?.id) {
        return (
          _.find(newBusiness, { id: cachedselectedBusiness.id }) ||
          _.first(newBusiness)
        );
      }
      return _.first(newBusiness) || null;
    };
    let selectedBusiness = getSelectedBusiness();

    // let selectedBusiness = getSelectedBusiness();
    if (
      cachedUser?.id &&
      !pathDoesNotRequireBusinessId(window.location.pathname)
    ) {
      const bidFromURL = getBIDFromURL(window.location.pathname);
      if (bidFromURL) {
        const isBizPermitted = _.find(
          forAdmin?.businesses || newBusiness || [],
          { id: bidFromURL }
        );
        if (!isBizPermitted) {
          setIsBusinessPermitted(false);
        } else {
          selectedBusiness = isBizPermitted;
        }
      }
    }
    if (selectedBusiness?.id && cachedUser?.id) {
      const res = await getBusinessDetails(cachedUser.id, selectedBusiness.id);

      if (res && res.status) {
        selectedBusiness = res.data;
      } else {
        toast.error(res?.message || "Failed to fetch business data");
        if (res?.message === "Please log in to continue.") logOut();
      }
      addUpdateCache({
        selectedBusiness: JSON.stringify({ id: selectedBusiness.id }),
      });
    }

    setAuth({
      user: cachedUser,
      newBusiness,
      selectedBusiness,
      forAdmin,
    });

    setInitializing(false);
  }, []);

  // const apiKeyLogin = async (username, password, businessId) => {
  //   const loginResponse = await callApi("/login", "POST", {
  //     username,
  //     password: sha256(password),
  //   });
  //   if (loginResponse?.status !== true) {
  //     return {
  //       success: false,
  //       message: loginResponse?.message,
  //     };
  //   }
  //   const { user: userFromResponse, business: businessFromResponse } =
  //     loginResponse.data;

  //   const authState = {
  //     user: userFromResponse,
  //     newBusiness: businessFromResponse,
  //   };
  //   if (userFromResponse && userFromResponse.role === "ADMIN") {
  //     const { admin } = loginResponse.data;
  //     if (admin.users && admin.businesses) {
  //       authState.forAdmin = {
  //         users: admin.users,
  //         businesses: admin.businesses,
  //       };
  //     }
  //   }

  //   const bidFromRedirectURL = businessId;
  //   let bizIdToSelect;
  //   if (bidFromRedirectURL) {
  //     bizIdToSelect =
  //       userFromResponse.role === "ADMIN"
  //         ? _.find(loginResponse.data?.admin?.businesses, {
  //             id: bidFromRedirectURL,
  //           })?.id
  //         : _.find(businessFromResponse, {
  //             id: bidFromRedirectURL,
  //           })?.id || _.first(businessFromResponse)?.id;
  //   } else {
  //     bizIdToSelect = _.first(businessFromResponse)?.id;
  //   }
  //   if (+bizIdToSelect !== businessId) return { success: false };
  //   if (bizIdToSelect) {
  //     const res = await getBusinessDetails(userFromResponse.id, bizIdToSelect);
  //     if (res && res.status) {
  //       authState.selectedBusiness = res.data;
  //     } else {
  //       authState.selectedBusiness = _.first(businessFromResponse);
  //       return { success: false };
  //     }
  //   }
  //   addUpdateCache({
  //     business: JSON.stringify(businessFromResponse),
  //     user: JSON.stringify(userFromResponse),
  //     selectedBusiness: JSON.stringify({ id: authState.selectedBusiness?.id }),
  //   });

  //   setAuth(authState);
  //   return {
  //     success: true,
  //     message: "Sucessfully Logged In",
  //     uid: userFromResponse.id,
  //     data: loginResponse?.data,
  //   };
  // };

  const logIn = async (
    username,
    password,
    otpData,
    otpValidate,
    tfaValidate,
    phoneAuthToken
  ) => {
    const loginResponse = tfaValidate
      ? await callApi("/login/dashboard/otp/validate", "POST", {
          tfa: tfaValidate.tfa,
          pin: tfaValidate.pin,
        })
      : !otpValidate
      ? !otpData
        ? await callApi("/dashboard/login", "POST", {
            username,
            password: sha256(password),
            ...(phoneAuthToken && { tfa: phoneAuthToken.tfa }), 
          })
        : await callApi(`/login/${otpData?.uid}/dashboard/otp`, "POST", {
            admin: otpData?.admin,
            key: otpData?.key,
            pin: otpData?.pin,
          })
      : await callApi("/login/dashboard/otp/validate", "POST", {
          phone: otpValidate.phone,
          pin: otpValidate.pin,
        });

    if (!loginResponse || loginResponse?.status !== true) {
      if (otpValidate) {
        toast.error(loginResponse?.message || "Unknown Error Occurred");
      }
      return {
        success: false,
        message: otpValidate ? "" : loginResponse?.message,
      };
    }
    if (loginResponse.data?.tfa) {
      if (loginResponse.data?.lastfour) {
        return {
          requiresTFA: true,
          ...loginResponse.data,
        };
      }

      return {
        awaitingEmailAuth : true,
        ...loginResponse.data,
      };
    }

    const { user: userFromResponse, business: businessFromResponse } =
      loginResponse.data;

    //   if(userFromResponse.role !== "ADMIN") {

    //   }else {
    //     businessFound = bidFromRedirectURL
    //       ? _.find(businessFromResponse, {
    //           id: bidFromRedirectURL,
    //         }) || _.first(businessFromResponse)
    //       : _.first(businessFromResponse)
    //   }
    // } else {
    //   businessFound = _.first(businessFromResponse)
    // }
    // const businessFound = bidFromRedirectURL
    //   ? _.find(businessFromResponse, {
    //       id: bidFromRedirectURL,
    //     }) || _.first(businessFromResponse)
    //   : _.first(businessFromResponse);
    const authState = {
      user: userFromResponse,
      newBusiness: businessFromResponse,
    };
    if (userFromResponse && userFromResponse.role === "ADMIN") {
      const { admin } = loginResponse.data;
      if (admin.users && admin.businesses) {
        authState.forAdmin = {
          users: admin.users,
          businesses: admin.businesses,
        };
      }
    }

    const bidFromRedirectURL = getBIDFromCachedRedirectURL();
    let bizIdToSelect;
    if (bidFromRedirectURL) {
      bizIdToSelect =
        userFromResponse.role === "ADMIN"
          ? _.find(loginResponse.data?.admin?.businesses, {
              id: bidFromRedirectURL,
            })?.id
          : _.find(businessFromResponse, {
              id: bidFromRedirectURL,
            })?.id || _.first(businessFromResponse)?.id;
    } else {
      bizIdToSelect = _.first(businessFromResponse)?.id;
    }
    if (bizIdToSelect) {
      const res = await getBusinessDetails(userFromResponse.id, bizIdToSelect);
      if (res && res.status) {
        authState.selectedBusiness = res.data;
      } else {
        authState.selectedBusiness = _.first(businessFromResponse);
        toast.error(res.message || "Failed to fetch business data");
        if (res.message === "Please log in to continue.") logOut();
      }
    }
    addUpdateCache({
      business: JSON.stringify(businessFromResponse),
      user: JSON.stringify(userFromResponse),
      selectedBusiness: JSON.stringify({ id: authState.selectedBusiness?.id }),
    });

    setAuth(authState);
    return {
      success: true,
      message: "Successfully Logged In",
      uid: userFromResponse.id,
      data: loginResponse?.data,
    };
  };

  const requestOTP = async (cc, phone) => {
    const otpResponse = await callApi("/login/dashboard/otp/request", "POST", {
      cc,
      phone,
    });

    if (!otpResponse?.status) {
      toast.error(otpResponse?.message || "Unknown Error Occurred");
    }
    return otpResponse;
  };

  const validateOTP = (phone, pin) => logIn(null, null, null, { phone, pin });
  const validateTFA = (tfa, pin) => logIn(null, null, null, null, { tfa, pin });

  const sendResetToken = async (email) => {
    const response = await callApi("/password/forgot", "POST", {
      email,
    });
    if (response?.status) {
      toast.success(response.data?.message);
    } else {
      toast.error(response?.message || "Unknown Error Occurred");
    }
    return response;
  };

  const resetPassword = async ({ password, email, token }) => {
    const response = await callApi(`/password/${token}/reset`, "POST", {
      email,
      password: password && sha256(password),
    });
    if (response?.status) {
      toast.success(response.data?.message || "Password Changed Successfully");
    } else {
      toast.error(response?.message || "Unknown Error Occurred");
    }
    return response;
  };

  const runWithGuard = (fn) => {
    if (auth.user && auth.selectedBusiness) {
      fn();
    }
  };

  const stayInCurrentRoute = () => {
    setIsBusinessPermitted(true);
    window.location.replace(
      `${window.location.origin}/${auth.selectedBusiness.id}/home`
    );
  };

  const updateCurrentBusiness = (updatedInfo) => {
    if (auth.newBusiness && auth.selectedBusiness?.id) {
      const { newBusiness, selectedBusiness } = auth;
      const index = newBusiness.findIndex((e) => e.id === selectedBusiness.id);
      const allBusinesses = newBusiness;
      allBusinesses[index] = { ...allBusinesses[index], ...updatedInfo };
      addUpdateCache({
        business: JSON.stringify(allBusinesses),
      });
      setAuth({
        ...auth,
        newBusiness: allBusinesses,
        selectedBusiness: updatedInfo,
      });
    }
  };

  const addUserByAdmin = (userDetails) => {
    const temp = auth;
    temp.forAdmin = {
      ...(auth.forAdmin || {}),
      users: [...(auth?.forAdmin?.users || []), userDetails],
    };
    setAuth({ ...temp });
  };

  const updateAdminUser = (userDetails) => {
    const temp = auth;
    temp.forAdmin = {
      ...(auth.forAdmin || {}),
      users: auth?.forAdmin?.users?.map((x) =>
        x.id === userDetails.id ? userDetails : x
      ),
    };
    setAuth({ ...temp });
  };

  const updateAdminBusiness = (bid, data) => {
    const temp = auth;
    temp.forAdmin = {
      ...(auth.forAdmin || {}),
      businesses: auth?.forAdmin?.businesses?.map((x) =>
        x.id === bid ? data : x
      ),
    };
    setAuth({ ...temp });
  };

  const updateBusinessList = (business) => {
    addUpdateCache({
      business: JSON.stringify(business),
    });
    setAuth({ ...auth, newBusiness: business });
  };

  const updateCurrentUser = (user) => {
    addUpdateCache({
      user: JSON.stringify({ ...auth.user, ...user }),
    });
    const temp = auth;
    temp.user = { ...auth.user, ...user };
    setAuth({ ...temp });
  };

  useEffect(() => {
    window.gz_global_dashboard_vars_currency_symbol =
      auth?.selectedBusiness?.info?.translationData?.currencySymbol;
  }, [auth]);

  return {
    isBusinessPermitted,
    user: auth.user,
    logIn,
    requestOTP,
    validateOTP,
    validateTFA,
    sendResetToken,
    resetPassword,
    logOut,
    isUserLoggedIn: () => auth.user,
    isAdmin: auth.user && auth.user.role === "ADMIN",
    stayInCurrentRoute,
    business: auth.newBusiness,
    selectedBusiness: auth.selectedBusiness,
    setSelectedBusiness: (u) => setAuthData("selectedBusiness", u),
    selectedBusinessDetail: auth.selectedBusiness,
    timezone: auth.selectedBusiness?.business?.timezone,
    // _.find(
    //   auth.newBusiness || [],
    //   (b) => b.id === auth.selectedBusiness?.id
    // ) ?? null,
    runWithGuard,
    admin: auth.forAdmin,
    updateCurrentBusiness,
    updateAdminUser,
    updateAdminBusiness,
    addUserByAdmin,
    initializing,
    setIsBusinessPermitted,
    getBusinessDetails,
    updateBusinessList,
    updateCurrentUser,
    multilocation:
      auth.forAdmin?.businesses?.length || auth?.newBusiness?.length,
    // apiKeyLogin,
  };
};

export const AuthProvider = ({ children }) => {
  const auth = useAuthProvider();
  const {
    initializing,
    isBusinessPermitted,
    stayInCurrentRoute,
    logOut,
    setIsBusinessPermitted,
  } = auth;
  return initializing ? (
    <SpinnerComponent />
  ) : !isBusinessPermitted ? (
    <BusinessNotPermitted
      open
      onStay={stayInCurrentRoute}
      onSwitch={async () => {
        await logOut();
        setIsBusinessPermitted(true);
      }}
    />
  ) : (
    <Provider value={auth}>{children}</Provider>
  );
};

export const useAuth = () => useContext(authContext);
